export type ServiceKey =
  | "hotel"
  | "hotels"
  | "rental"
  | "rentals"
  | "spa_msg"
  | "tour_act"
  | "activities"
  | "flights"
  | "esim"
  | "private_taxi"
  | "other_service"
  | "discover"
  | "deals";

type SidebarTablerIcon =
  "hotel" | "flight" | "rental" | "spa" | "tour" | "esim";

export type MainServiceLabel =
  "Hotels" | "Flights" | "Rentals" | "Spas & Massages" | "Activities" | "eSim";

export type IntegrationLinkKey = "bookmefly" | "knowroaming";

const INTERNAL_SERVICE_PATHS = {
  hotel: "/hotels",
  rental: "/rentals",
  spa_msg: "/spa-msg",
  tour_act: "/tour-act",
  discover: "/discover",
  deals: "/deals",
} as const;

export type ServiceCatalogItem = {
  key: ServiceKey;
  label: string;
  sidebarLabel?: string;
  routePath?: string;
  integrationKey?: IntegrationLinkKey;
  matchPaths?: string[];
  showInTopNav?: boolean;
  sidebarTablerIcon?: SidebarTablerIcon;
};

// Clean & DRY: key is the single source of truth (no redundant queryValue or href)
export const SERVICE_CATALOG = [
  {
    key: "hotel",
    label: "Accommodations",
    routePath: INTERNAL_SERVICE_PATHS.hotel,
    showInTopNav: true,
    sidebarTablerIcon: "hotel",
  },
  {
    key: "rental",
    label: "Rentals",
    routePath: INTERNAL_SERVICE_PATHS.rental,
    showInTopNav: true,
    sidebarTablerIcon: "rental",
  },
  {
    key: "spa_msg",
    label: "Spas & Massages",
    routePath: INTERNAL_SERVICE_PATHS.spa_msg,
    showInTopNav: true,
    sidebarTablerIcon: "spa",
  },
  {
    key: "tour_act",
    label: "Activities",
    routePath: INTERNAL_SERVICE_PATHS.tour_act,
    showInTopNav: true,
    sidebarTablerIcon: "tour",
  },
  {
    key: "flights",
    label: "Flights",
    integrationKey: "bookmefly",
    matchPaths: ["/flight"],
    showInTopNav: true,
    sidebarTablerIcon: "flight",
  },
  {
    key: "esim",
    label: "eSim",
    integrationKey: "knowroaming",
    matchPaths: ["/esim"],
    showInTopNav: true,
    sidebarTablerIcon: "esim",
  },
  {
    key: "discover",
    label: "Discover",
    routePath: INTERNAL_SERVICE_PATHS.discover,
    showInTopNav: true,
  },
  // {
  //   key: "deals",
  //   label: "Tovtrip Deals",
  //   routePath: INTERNAL_SERVICE_PATHS.deals,
  //   showInTopNav: true,
  // },
  {
    key: "private_taxi",
    label: "Private Taxi",
  },
  {
    key: "other_service",
    label: "Other Service",
  },
] satisfies ServiceCatalogItem[];

export const getServiceHref = (
  service: ServiceCatalogItem,
): string | undefined => {
  return service.routePath;
};

export const getTopNavServices = () =>
  SERVICE_CATALOG.filter((service) => service.showInTopNav);

export const getServiceByRoute = (
  pathname: string,
):
  | (ServiceCatalogItem & { label: MainServiceLabel; routePath: string })
  | undefined => {
  const service = SERVICE_CATALOG.find(
    (item) =>
      item.routePath &&
      (item.routePath === pathname ||
        pathname.startsWith(`${item.routePath}/`) ||
        pathname.includes(`${item.routePath}/`)),
  );
  if (!service?.routePath) return undefined;
  return service as ServiceCatalogItem & {
    label: MainServiceLabel;
    routePath: string;
  };
};

export const getServiceByQueryValue = (
  queryValue: string,
): (ServiceCatalogItem & { label: MainServiceLabel }) | undefined => {
  const service = SERVICE_CATALOG.find(
    (item) =>
      item.key === queryValue ||
      (queryValue === "hotels" && item.key === "hotel") ||
      (queryValue === "rentals" && item.key === "rental") ||
      (queryValue === "activities" && item.key === "tour_act"),
  );
  if (!service) return undefined;
  return service as ServiceCatalogItem & {
    label: MainServiceLabel;
  };
};

export const getSearchPathByLabel = (
  labelOrKey: MainServiceLabel | string,
): string | undefined =>
  (() => {
    if (!labelOrKey) return undefined;
    const normalized = labelOrKey.toLowerCase().trim().replace(/-/g, "_");
    const routePath = SERVICE_CATALOG.find(
      (service) =>
        service.label === labelOrKey ||
        service.key === labelOrKey ||
        service.label.toLowerCase() === normalized ||
        service.key.toLowerCase() === normalized ||
        (normalized.includes("hotel") && service.key === "hotel") ||
        (normalized.includes("rental") && service.key === "rental") ||
        (normalized.includes("spa") && service.key === "spa_msg") ||
        ((normalized.includes("tour") || normalized.includes("activit")) &&
          service.key === "tour_act"),
    )?.routePath;
    return routePath ? `${routePath}/search` : undefined;
  })();

export const getQueryValueByLabel = (
  label: MainServiceLabel | string,
): string | undefined =>
  SERVICE_CATALOG.find(
    (service) => service.label === label || service.key === label,
  )?.key;
